import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ViewModeProvider, useViewMode } from './context/ViewModeContext';
import { ConfirmProvider } from './context/ConfirmContext';
import { MainLayout } from './components/layout/MainLayout';
import { MobileLayout } from './components/layout/MobileLayout';
import { LandingPage } from './pages/LandingPage';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { PrivacyPage } from './pages/legal/PrivacyPage';
import { ImpressumPage } from './pages/legal/ImpressumPage';
import { DashboardPage } from './pages/DashboardPage';
import { CustomersPage } from './pages/CustomersPage';
import { CustomerDetailPage } from './pages/CustomerDetailPage';
import { ItemsPage } from './pages/ItemsPage';
import { WorkOrdersPage } from './pages/WorkOrdersPage';
import { WorkOrderDetailPage } from './pages/WorkOrderDetailPage';
import { InvoicesPage } from './pages/InvoicesPage';
import { InvoiceGeneratePage } from './pages/InvoiceGeneratePage';
import { InvoiceDetailPage } from './pages/InvoiceDetailPage';
import { PersonnelPage } from './pages/PersonnelPage';
import { SettingsPage } from './pages/SettingsPage';
// Mobile pages
import { MobileHomePage } from './pages/mobile/MobileHomePage';
import { MobileWorkOrdersPage } from './pages/mobile/MobileWorkOrdersPage';
import { MobileWorkOrderFormPage } from './pages/mobile/MobileWorkOrderFormPage';
import { MobileProfilePage } from './pages/mobile/MobileProfilePage';
import {
  SuperAdminLoginPage,
  SuperAdminLayout,
  SuperAdminDashboard,
  TenantsPage,
  TenantDetailPage,
  PlansPage
} from './pages/superadmin';
import { Toaster } from './components/ui/sonner';
import '@/App.css';

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-slate-600">Caricamento...</p>
        </div>
      </div>
    );
  }

  return user ? children : <Navigate to="/login" />;
};

// Redirect desktop to mobile when on mobile device or when role is SHARED (kiosk)
const DesktopRouteGuard = ({ children }) => {
  const { shouldShowMobile } = useViewMode();
  const { user } = useAuth();
  const isShared = user?.is_shared || user?.role === 'SHARED';
  if (shouldShowMobile || isShared) {
    return <Navigate to="/m" replace />;
  }
  return children;
};

// Smart redirect based on device + role
const SmartRedirect = () => {
  const { shouldShowMobile } = useViewMode();
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  const isShared = user?.is_shared || user?.role === 'SHARED';
  if (shouldShowMobile || isShared) return <Navigate to="/m" replace />;
  return <Navigate to="/app/dashboard" replace />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Pages — Landing nascosta in attesa di Stripe; root reindirizza al login */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="/landing" element={<LandingPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/impressum" element={<ImpressumPage />} />

      {/* Super Admin Routes */}
      <Route path="/super-admin/login" element={<SuperAdminLoginPage />} />
      <Route path="/super-admin" element={<SuperAdminLayout />}>
        <Route index element={<SuperAdminDashboard />} />
        <Route path="tenants" element={<TenantsPage />} />
        <Route path="tenants/:id" element={<TenantDetailPage />} />
        <Route path="plans" element={<PlansPage />} />
      </Route>

      {/* Login */}
      <Route path="/login" element={<LoginPage />} />

      {/* Mobile Routes */}
      <Route
        path="/m"
        element={
          <PrivateRoute>
            <MobileLayout />
          </PrivateRoute>
        }
      >
        <Route index element={<MobileHomePage />} />
        <Route path="bollettini" element={<MobileWorkOrdersPage />} />
        <Route path="bollettini/nuovo" element={<MobileWorkOrderFormPage />} />
        <Route path="bollettini/:id" element={<MobileWorkOrderFormPage />} />
        <Route path="profilo" element={<MobileProfilePage />} />
      </Route>

      {/* Desktop Tenant App Routes */}
      <Route
        path="/app"
        element={
          <PrivateRoute>
            <DesktopRouteGuard>
              <MainLayout />
            </DesktopRouteGuard>
          </PrivateRoute>
        }
      >
        <Route index element={<Navigate to="/app/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="customers" element={<CustomersPage />} />
        <Route path="customers/:id" element={<CustomerDetailPage />} />
        <Route path="items" element={<ItemsPage />} />
        <Route path="work-orders" element={<WorkOrdersPage />} />
        <Route path="work-orders/:id" element={<WorkOrderDetailPage />} />
        <Route path="invoices" element={<InvoicesPage />} />
        <Route path="invoices/generate" element={<InvoiceGeneratePage />} />
        <Route path="invoices/:id" element={<InvoiceDetailPage />} />
        <Route path="personnel" element={<PersonnelPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      {/* Redirect old routes */}
      <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />
      <Route path="/customers/*" element={<Navigate to="/app/customers" replace />} />
      <Route path="/items" element={<Navigate to="/app/items" replace />} />
      <Route path="/work-orders/*" element={<Navigate to="/app/work-orders" replace />} />
      <Route path="/invoices/*" element={<Navigate to="/app/invoices" replace />} />
      <Route path="/personnel" element={<Navigate to="/app/personnel" replace />} />
      <Route path="/settings" element={<Navigate to="/app/settings" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <ViewModeProvider>
        <ConfirmProvider>
          <BrowserRouter>
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </ConfirmProvider>
      </ViewModeProvider>
    </AuthProvider>
  );
}

export default App;
